/**
 * SubServicePage.tsx — Phase 3 universal renderer for sub-service leaf pages.
 * Consumes an entry from `SUBSERVICE_POOL` and emits an A5-quality page with
 * Service + FAQPage + HowTo + Speakable JSON-LD (no Review/Rating).
 */
import { Link } from "@tanstack/react-router";
import { MessageCircle, Phone, CheckCircle2, Sparkles, ArrowRight, FileText } from "lucide-react";
import { SUBSERVICE_POOL, type SubServiceContent } from "@/data/subservice-content";
import { LeadFormModal } from "@/components/LeadFormModal";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";

type Locale = "th" | "en";

export function SubServicePage({ slug, locale }: { slug: string; locale: Locale }) {
  const entry = SUBSERVICE_POOL[slug];
  if (!entry) return null;
  const c = entry[locale];
  const pageUrl = `${SITE}${locale === "en" ? "/en" : ""}/${slug}`;
  const home = locale === "th" ? "/" : "/en";
  const parent = locale === "th" ? `/${entry.parentSlug}` : `/en/${entry.parentSlug}`;
  const parentLabel = locale === "th" ? entry.parentLabel : entry.parentLabelEn;
  const t = locale === "th"
    ? {
        home: "หน้าแรก",
        quickAnswer: "คำตอบเร็ว",
        stats: "ตัวเลขที่ควรรู้",
        steps: "ขั้นตอนการทำงาน",
        faq: "คำถามที่พบบ่อย",
        related: "บริการที่เกี่ยวข้อง",
        contact: "ติดต่อทีมของเรา",
        line: "LINE @nycli",
        phone: "083-249-4999",
      }
    : {
        home: "Home",
        quickAnswer: "Quick answer",
        stats: "Key numbers",
        steps: "How it works",
        faq: "FAQ",
        related: "Related services",
        contact: "Talk to our team",
        line: "LINE @nycli",
        phone: "+66 83-249-4999",
      };

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a1a3d] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(251,191,36,0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/70">
            <Link to={home} className="hover:text-amber-300">{t.home}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to={parent} className="hover:text-amber-300">{parentLabel}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/90">{c.h1.split("—")[0].trim()}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <Sparkles className="h-3.5 w-3.5" /> NYC · A5 Service
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">{c.h1}</h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/85 sm:text-lg" data-speakable>{c.hero}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:bg-emerald-500">
              <MessageCircle className="h-4 w-4" /> {t.line}
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-[#0a1a3d] shadow-md hover:bg-amber-300">
              <Phone className="h-4 w-4" /> {t.phone}
            </a>
          </div>
          <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {c.stats.map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <dt className="text-[11px] uppercase tracking-widest text-amber-300">{s.label}</dt>
                <dd className="mt-1 font-display text-2xl font-extrabold">{s.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Quick answer */}
      <section className="border-b border-slate-200 bg-amber-50/40">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700">{t.quickAnswer}</div>
          <p className="mt-3 text-lg leading-relaxed text-slate-800" data-speakable>{c.quickAnswer}</p>
        </div>
      </section>

      {/* Deep sections */}
      <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-16">
        {c.sections.map((sec) => (
          <article key={sec.heading} className="mb-10">
            <h2 className="font-display text-2xl font-extrabold text-[#0a1a3d] sm:text-3xl">{sec.heading}</h2>
            <p className="mt-3 leading-relaxed text-slate-700">{sec.body}</p>
          </article>
        ))}
      </section>

      {/* Steps */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-16">
          <h2 className="font-display text-2xl font-extrabold text-[#0a1a3d] sm:text-3xl">{t.steps}</h2>
          <ol className="mt-6 grid gap-4 sm:grid-cols-2">
            {c.steps.map((s) => (
              <li key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  <div>
                    <div className="font-bold text-[#0a1a3d]">{s.title}</div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">{s.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-16">
        <h2 className="font-display text-2xl font-extrabold text-[#0a1a3d] sm:text-3xl">{t.faq}</h2>
        <div className="mt-6 space-y-4">
          {c.faqs.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-slate-200 bg-white p-5">
              <summary className="cursor-pointer list-none font-bold text-[#0a1a3d]">
                <span className="inline-flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-amber-600 transition-transform group-open:rotate-90" />
                  {f.q}
                </span>
              </summary>
              <p className="mt-3 leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <h2 className="font-display text-xl font-extrabold text-[#0a1a3d] sm:text-2xl">{t.related}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {c.relatedSlugs.map((path) => (
              <li key={path}>
                <a
                  href={path}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-[#0a1a3d] hover:border-amber-400 hover:text-amber-700"
                >
                  <ArrowRight className="h-4 w-4 text-amber-600" />
                  {path}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-[#0a1a3d] text-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 text-center">
          <h2 className="font-display text-2xl font-extrabold sm:text-3xl">{t.contact}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/75 sm:text-base">
            {locale === "th"
              ? "กรอกฟอร์มเพื่อรับใบเสนอราคาเฉพาะบริการนี้ ทีมงานติดต่อกลับผ่าน LINE ภายใน 15 นาทีในเวลาทำการ"
              : "Send a quick brief for this exact service. Our team replies via LINE within 15 minutes during business hours."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              defaultService={c.h1}
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#0a1a3d] shadow-md hover:bg-amber-50"
                >
                  <FileText className="h-4 w-4" />
                  {locale === "th" ? "ขอใบเสนอราคา" : "Request quote"}
                </button>
              }
            />
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:bg-emerald-500">
              <MessageCircle className="h-4 w-4" /> {t.line}
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-[#0a1a3d] shadow-md hover:bg-amber-300">
              <Phone className="h-4 w-4" /> {t.phone}
            </a>
          </div>
        </div>
      </section>

      <RelatedHubGrid locale={locale} />

      {/* Structured data (body-level JSON-LD keeps head() out of the critical bundle) */}
      {subServiceGraph(entry, locale, pageUrl).map((obj, i) => (
        <script
          key={i}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }}
        />
      ))}
    </main>
  );
}

export function subServiceGraph(entry: SubServiceContent, locale: Locale, url: string) {
  const c = entry[locale];
  const inLanguage = locale === "th" ? "th-TH" : "en-US";
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${url}#service`,
      name: c.h1,
      description: c.metaDesc,
      inLanguage,
      areaServed: { "@type": "Country", name: "Thailand" },
      provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage,
      mainEntity: c.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: c.h1,
      inLanguage,
      step: c.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      name: c.h1,
      description: c.metaDesc,
      inLanguage,
      url,
      isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website`, url: SITE },
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", "h2"] },
    },
  ];
}
